import {
  AppBar,
  Badge,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import CopyableTooltip from 'components/copyable-tooltip'
import Logo from 'components/logo'
import Logout from 'components/logout'
import { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { NavLink, Outlet } from 'react-router'
import {
  Notifications as NotificationsIcon,
  HelpOutlined as HelpOutlinedIcon,
  Delete as DeleteIcon,
  PriorityHigh as PriorityHighIcon,
  LowPriority as LowPriorityIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import ImpersonateCheck from 'components/impersonate-check'
import colors from 'common/colors'
import { alpha } from '@mui/material/styles'
import {
  dismissNotifications,
  readNotifications,
  readAllNotifications,
  dismissAllNotifications,
} from 'api/notification'
import { showModal, closeModal } from 'actions/app'
import DOMPurify from 'dompurify'
import { fetchNotifications } from 'api/notification'
import CircularProgress from '@mui/material/CircularProgress'
import React from 'react'
import { TEST_IDS } from 'test-ids'
import { FEATURE_FLAGS } from 'feature-flags'
import FeatureFlag from 'components/feature-flag'

function getLinkStyle(isActive) {
  return isActive ? activeStyle : farmLinksStyle
}

const activeStyle = {
  borderRadius: '4px',
  textDecoration: 'none',
  color: colors.brandSecondary,
  width: 'inherit',
  fontWeight: 'bold',
  borderBottom: `2px solid ${colors.brandSecondary}`,
}

const farmLinksStyle = {
  color: 'rgba(255, 255, 255, 0.75)',
  textDecoration: 'none',
  display: 'flex',
  width: 'inherit',
  whiteSpace: 'nowrap',
  borderBottom: '2px solid transparent',
}

export default function Header() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const permissions = useSelector((state) => state.account.permissions)
  const notifications = useSelector((state) => state.notification.collection)
  const moreNotifications = useSelector((state) => state.notification.hasMore)
  const unreadCount = useSelector((state) => state.notification.unreadCount)
  const [showFields, setShowField] = useState(false)
  const [showReports, setShowReports] = useState(false)
  const [showAdmin, setShowAdmin] = useState(false)
  const [showOrders, setShowOrders] = useState(false)
  const [showSupport] = useState(false)
  const [showFarmSetup, setShowFarmSetup] = useState(false)
  const [allowDismissNotifications, setAllowDismissNotifications] =
    useState(false)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)
  const [notificationsIconEl, setNotificationsIconEl] = React.useState(null)
  const notificationsOpen = Boolean(notificationsIconEl)
  const [unreadNotifications, setUnreadNotifications] = useState(null)
  const tenantObj = useSelector((state) => state.account.singleton)
  const [notificationsMarkedAsRead, setNotificationsMarkedAsRead] =
    useState(false)
  const language = useSelector(
    (state) => state.account.singleton?.prefs?.general?.language,
  )

  const [notificationsPage, setNotificationsPage] = useState(1)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (notifications.length) {
      setUnreadNotifications(notifications.filter((n) => !n.isRead))
    } else {
      setUnreadNotifications([])
    }
  }, [notifications])

  const handleNotificationsClick = (event) => {
    setNotificationsIconEl(event.currentTarget)
  }

  const handleNotificationsClose = () => {
    setNotificationsIconEl(null)
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const onHelp = () => {
    return window.open(`${HELPGUIDE_URL}`, '_blank')
  }

  const checkForHTML = (message) => {
    let tags = ['<!DOCTYPE html>', '<html>', '<title>', '<body>']
    let value = new RegExp(tags.join('|')).test(message)
    return value
  }

  const getMessage = (message) => {
    let value = checkForHTML(message)
    if (value) {
      const domParser = new DOMParser()
      var docElement = domParser.parseFromString(
        message,
        'text/html',
      ).documentElement
      let title = docElement.getElementsByTagName('title')[0].innerText
      return title
    } else {
      return message
    }
  }

  const getNotificationCreatedAt = useCallback(
    (createdAt) => {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      let localeDateTime = new Intl.DateTimeFormat(
        language ?? navigator.language,
        {
          timeZone,
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        },
      ).format(new Date(createdAt))
      return localeDateTime
    },
    [language],
  )

  const showNotificationDialog = (id, message) => () => {
    let title = getMessage(message)
    dispatch(
      showModal(<NotificationDialog id={id} message={message} />, {
        title,
      }),
    )
    handleNotificationsClose()
  }

  const NotificationDialog = ({ id, message }) => {
    return (
      <>
        <div
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }}
        ></div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              dismissItems(id)
              dispatch(closeModal())
            }}
          >
            {t('dismiss')}
          </Button>
        </div>
      </>
    )
  }

  const markAsRead = (id) => {
    if (!notificationsMarkedAsRead) {
      if (Number.isInteger(parseInt(id))) {
        setNotificationsMarkedAsRead(true)
        setTimeout(() => {
          dispatch(readNotifications([id]))
          setNotificationsMarkedAsRead(false)
        }, 2000)
      }
    }
  }

  const markAllAsRead = () => {
    dispatch(readAllNotifications())
  }

  const dismissItems = useCallback((id) => {
    if (Number.isInteger(parseInt(id))) {
      dispatch(dismissNotifications([id]))
    }
  })

  const dismissAllItems = () => {
    dispatch(dismissAllNotifications())
  }

  useEffect(() => {
    setShowField(permissions.includes('field:read'))
    setShowReports(permissions.includes('reports:read'))
    setShowAdmin(
      permissions.includes('users:write') ||
        permissions.includes('tenant:list'),
    )
    setShowOrders(permissions.includes('order:write'))
    setShowFarmSetup(permissions.includes('farm:read'))
    setAllowDismissNotifications(permissions.includes('farm:write'))
  }, [permissions])

  const onScrollNotifications = (e) => {
    if (loading) return
    const reachedBottom =
      e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - 10
    if (reachedBottom && moreNotifications) {
      setLoading(true)
      let pageNumber = notificationsPage + 1
      dispatch(fetchNotifications(pageNumber))
      setNotificationsPage(pageNumber)
    }
  }

  useEffect(() => {
    if (loading) {
      setLoading(false)
    }
  }, [notifications])

  return (
    <div>
      <AppBar
        position="static"
        color="primary"
        sx={{
          // MUI never gives a "static" AppBar a z-index, so a permanent
          // Drawer's fixed-position Paper (CommonSidebar) would otherwise
          // paint over it. Make the header a positioned stacking context
          // above any drawer so it always paints on top.
          position: 'relative',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar variant="dense">
          <Logo />
          <div style={{ marginRight: 1, flexGrow: 2 }}>
            <List style={{ display: 'flex' }}>
              {showFields && (
                <ListItem style={{ textAlign: 'center' }}>
                  <NavLink
                    style={({ isActive }) => getLinkStyle(isActive)}
                    to="/"
                  >
                    <ListItemText disableTypography primary={t('map')} />
                  </NavLink>
                </ListItem>
              )}
              {showFarmSetup && (
                <ListItem style={{ textAlign: 'center' }}>
                  <NavLink
                    to="/fields"
                    style={({ isActive }) => getLinkStyle(isActive)}
                  >
                    <ListItemText disableTypography primary={t('fields')} />
                  </NavLink>
                </ListItem>
              )}
              {showReports && (
                <ListItem style={{ textAlign: 'center' }}>
                  <NavLink
                    to="/reports"
                    style={({ isActive }) => getLinkStyle(isActive)}
                  >
                    <ListItemText disableTypography primary={t('reports')} />
                  </NavLink>
                </ListItem>
              )}
              {showSupport && (
                <ListItem style={{ textAlign: 'center' }}>
                  <NavLink
                    to="/support"
                    style={({ isActive }) => getLinkStyle(isActive)}
                  >
                    <ListItemText disableTypography primary={t('support')} />
                  </NavLink>
                </ListItem>
              )}
              {showFarmSetup && (
                <ListItem style={{ textAlign: 'center' }}>
                  <NavLink
                    to="/farmSetup"
                    style={({ isActive }) => getLinkStyle(isActive)}
                  >
                    <ListItemText
                      style={{ whiteSpace: 'nowrap' }}
                      disableTypography
                      primary={t('farm_setup')}
                    />
                  </NavLink>
                </ListItem>
              )}
              {showAdmin && (
                <ListItem style={{ textAlign: 'center' }}>
                  <NavLink
                    to="/admin"
                    style={({ isActive }) => getLinkStyle(isActive)}
                  >
                    <ListItemText disableTypography primary={t('admin')} />
                  </NavLink>
                </ListItem>
              )}
              <FeatureFlag featureName={FEATURE_FLAGS.orders}>
                {showOrders && (
                  <ListItem
                    style={{ textAlign: 'center' }}
                    data-testid={TEST_IDS.ordersNav}
                  >
                    <NavLink
                      to="/orders"
                      style={({ isActive }) => getLinkStyle(isActive)}
                    >
                      <ListItemText disableTypography primary={t('orders')} />
                    </NavLink>
                  </ListItem>
                )}
              </FeatureFlag>
            </List>
          </div>
          <div
            style={{
              display: 'flex',
              flexGrow: '5',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {permissions.includes('tenant:list') ? (
              <CopyableTooltip content={tenantObj?.id}>
                <li
                  style={{
                    padding: '8px',
                    listStyle: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <b>{tenantObj?.['displayName']}</b>
                </li>
              </CopyableTooltip>
            ) : (
              <li style={{ padding: '8px', listStyle: 'none' }}>
                <b>{tenantObj?.['displayName']}</b>
              </li>
            )}

            <ImpersonateCheck />
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ borderColor: 'rgba(255, 255, 255, 0.3)' }}
            />

            <Tooltip title={t('notifications')} disableInteractive>
              <Badge
                badgeContent={unreadCount}
                color="secondary"
                sx={{
                  // IconButton's sizeSmall padding (theme.js) grows the
                  // button's real box to give it a proper touch target, but
                  // Badge anchors to that whole box, not the visible icon —
                  // left at the default top:0/right:0 the dot sits 10px
                  // (the added padding) higher/righter than the icon itself,
                  // pushing it off the top of the AppBar, which has no room
                  // above it. Inset by that same 10px to re-anchor the dot
                  // to the icon's corner instead of the padded button's.
                  '& .MuiBadge-badge': {
                    top: 10,
                    right: 10,
                  },
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleNotificationsClick}
                  data-testid={TEST_IDS.notificationsButton}
                >
                  <NotificationsIcon sx={{ color: colors.white }} />
                </IconButton>
              </Badge>
            </Tooltip>

            <Menu
              key="notifications"
              anchorEl={notificationsIconEl}
              id={TEST_IDS.notificationsDropdownMenu}
              data-testid={TEST_IDS.notificationsDropdownMenu}
              open={notificationsOpen}
              onClose={handleNotificationsClose}
              elevation={0}
              slotProps={{
                paper: {
                  style: {
                    width: '300px',
                  },
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 8,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  ml: 1,
                  mb: 1,
                  justifyContent: 'center',
                  display: notifications?.length ? 'flex' : 'none',
                  position: 'sticky',
                  top: 0,
                  bottom: 0,
                  // Needs to stay opaque — it's sticky, occluding the list
                  // scrolling underneath. Matches Paper's own background
                  // exactly now that the menu's `elevation={0}` (previously
                  // misplaced inside `slotProps`, where it had no effect —
                  // see the Menu prop below) actually takes effect, so
                  // there's no M3 elevation tint on this Paper to mismatch.
                  backgroundColor: colors.white,
                  marginLeft: 'unset',
                  zIndex: 1,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={markAllAsRead}
                  disabled={unreadNotifications?.length == 0}
                >
                  {t('mark_all_read')}
                </Button>
                {allowDismissNotifications ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={dismissAllItems}
                  >
                    {t('dismiss_all')}
                  </Button>
                ) : null}
              </Stack>
              {notifications?.length ? (
                <div
                  style={{ maxHeight: '300px', overflowY: 'auto' }}
                  onScroll={onScrollNotifications}
                >
                  {notifications.map((n, index) => (
                    <React.Fragment key={n?.id || index}>
                      <MenuItem
                        onMouseEnter={() =>
                          n?.isRead
                            ? null
                            : notificationsMarkedAsRead
                              ? null
                              : markAsRead(n.id)
                        }
                        sx={{
                          textWrap: 'auto',
                          cursor: 'default',
                          paddingLeft: '8px !important',
                          backgroundColor: n?.isRead
                            ? null
                            : alpha(colors.brand, 0.12),
                          '& .MuiTypography-root': n?.isRead
                            ? null
                            : {
                                fontWeight: 'bold',
                              },
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{ mr: 1, cursor: 'default' }}
                        >
                          {n.severity === 'important' ? (
                            <PriorityHighIcon />
                          ) : (
                            <LowPriorityIcon />
                          )}
                        </IconButton>
                        <Typography sx={{ width: '80%' }}>
                          {getMessage(n.message)}
                        </Typography>
                        {checkForHTML(n.message) ? (
                          <IconButton
                            size="small"
                            sx={{ ml: 'auto' }}
                            onClick={showNotificationDialog(n.id, n.message)}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        ) : null}
                        {allowDismissNotifications ? (
                          <IconButton
                            onClick={() => dismissItems(n.id)}
                            sx={{
                              ml: checkForHTML(n.message) ? null : 'auto',
                            }}
                          >
                            <DeleteIcon titleAccess="Dismiss" />
                          </IconButton>
                        ) : null}
                      </MenuItem>
                      <MenuItem
                        key={`timestamp-${n?.id || index}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'end',
                          padding: '0 8px 0 0',
                          cursor: 'default',
                        }}
                      >
                        <Typography variant="caption" gutterBottom>
                          {getNotificationCreatedAt(n.createdAt)}
                        </Typography>
                      </MenuItem>
                      <Divider
                        key={`divider-${n?.id || index}`}
                        sx={{ margin: '0 !important' }}
                      />
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <MenuItem key="no-notifications" sx={{ textWrap: 'auto' }}>
                  <Typography>{t('no_notifications')}</Typography>
                </MenuItem>
              )}
              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '10px',
                  }}
                >
                  <CircularProgress />
                </div>
              ) : null}
            </Menu>

            <Tooltip title={t('help')} disableInteractive>
              <IconButton
                size="small"
                onClick={handleClick}
                data-testid={TEST_IDS.helpButton}
              >
                <HelpOutlinedIcon sx={{ color: colors.white }} />
              </IconButton>
            </Tooltip>
            <Menu
              key="help"
              anchorEl={anchorEl}
              id={TEST_IDS.helpMenu}
              data-testid={TEST_IDS.helpMenu}
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              elevation={0}
              slotProps={{
                paper: {
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 8,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={onHelp}>
                <ListItemIcon>
                  <OpenInNewIcon fontSize="small" />
                </ListItemIcon>
                {t('help')}
              </MenuItem>
            </Menu>

            <Divider />
            <Logout />
          </div>
          <Outlet />

          {/*<SettingsApplications color="primary" onClick={showPreferenceModal} />*/}
        </Toolbar>
      </AppBar>
    </div>
  )
}
